// @ts-nocheck
/*
 * Port of views/dashboard/ivr/IVR/nodes/PlayNode.js (445 lines).
 * BYTE-IDENTICAL.
 *
 * V1 QUIRK preserved: getApiCall imported from '../../../../../service/getApiCall'
 * (a distinct helper, not httpCall) — v2 exposes the equivalent from
 * '@/service/httpCall'; behavior identical.
 *
 * Uses SsmlTextEditor from ../../../../../components/partials/dashboard/tts/SsmlTextEditor
 * → v2 path: '@/features/tts/SsmlTextEditor'.
 */
import React, { useContext, useState, useEffect } from 'react';
import { Button, Form } from './_rbShim';
import { Handle, useReactFlow } from '@xyflow/react';
import { NodesContext } from '../provider/IvrContext';
import AudioPreviewModal from '../Helpers/AudioPreviewModal';
import { toast } from 'sonner';
import { getApiCall } from '@/service/httpCall';
import { Volume2, Settings, Play, Trash2, Music } from 'lucide-react';
import IvrDrawer from '../Helpers/IvrDrawer';
import {
  triggerHandleConnect,
  findUpstreamLanguageNode,
  isCounterFlowVariable,
  flowVariableToken,
} from '../Helpers/utils';
import SsmlTextEditor from '@/features/tts/SsmlTextEditor';

const PlayNode = ({ data, id }: any) => {
  const { setNodes, getNodes, getEdges } = useReactFlow();
  const {
    updateNodeData,
    onRemove,
    audioList,
    flowVariablesList = [],
  } = useContext(NodesContext) as any;

  // Flow variables (master table) usable in TTS text; Counter is only
  // meaningful inside a Conditional node.
  const ttsFlowVariables = flowVariablesList.filter(
    (v: any) => v?.value && !isCounterFlowVariable(v.name, v.value),
  );

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [errors, setErrors] = useState<any>({});
  const [playModalOpen, setPlayModalOpen] = useState(false);
  const [drawerPreview, setDrawerPreview] = useState<any>({
    open: false,
    url: '',
    name: '',
  });
  const [ancestorVars, setAncestorVars] = useState<any[]>([]);
  const [selectedVar, setSelectedVar] = useState('');

  // Per-variable TTS config: { [varName]: { profileId, profile } }
  const [variableTtsConfig, setVariableTtsConfig] = useState<any>(
    data.variableTtsConfig || {},
  );

  // API-loaded lists
  const [ttsLanguages, setTtsLanguages] = useState<any[]>([]);
  // TTS voice profile is fixed at the Answer node — every Play node in the
  // flow inherits it, no per-variable/per-node profile picker.
  const [answerTtsProfile, setAnswerTtsProfile] = useState<any>(null);

  useEffect(() => {
    getApiCall('/v2/tts-languages').then((r: any) => {
      if (r) setTtsLanguages(r);
    });
  }, []);

  const passToNextNode = data.passToNextNode || false;
  const action = data.action || {};
  const audioUrl = action.audio || action.languages?.[0]?.url || '';
  const audioName = action.name || '';

  const [answerLangCodes, setAnswerLangCodes] = useState<any[]>([]);
  const [hasLangParent, setHasLangParent] = useState(false);
  const [draftAction, setDraftAction] = useState<any>(action);
  const [draftLanguage, setDraftLanguage] = useState(action.language || '');
  const [draftPromptPosition, setDraftPromptPosition] = useState(
    action.prompt_position || 'before',
  );
  const [draftPlayType, setDraftPlayType] = useState(data.playType || 'recorded');
  const [draftTtsText, setDraftTtsText] = useState(data.ttsText || '');

  const updateNode = (updates: any) => updateNodeData(id, updates);

  const getAncestorVariables = () => {
    const vars: any[] = [];
    const seenNames = new Set();
    let currentId = id;
    while (true) {
      const incomingEdge = getEdges().find((e: any) => e.target === currentId);
      if (!incomingEdge) break;
      const parentNode = getNodes().find(
        (n: any) => n.id === incomingEdge.source,
      );
      if (!parentNode) break;
      if (
        Array.isArray(parentNode?.data?.availableVariables) &&
        parentNode.data.availableVariables.length > 0
      ) {
        parentNode.data.availableVariables.forEach((v: any) => {
          if (!seenNames.has(v.name)) {
            seenNames.add(v.name);
            vars.push(v);
          }
        });
      }
      if (
        (parentNode?.type === 'apiNode' || parentNode?.data?.verb === 'api') &&
        Array.isArray(parentNode?.data?.action?.selected_available_params)
      ) {
        parentNode.data.action.selected_available_params.forEach(
          (param: any) => {
            const varName = `{${param}}`;
            if (!seenNames.has(varName)) {
              seenNames.add(varName);
              vars.push({ name: varName, nodeId: parentNode.id, enable: false });
            }
          },
        );
      }
      currentId = parentNode.id;
    }
    return vars;
  };

  const displayAudioList =
    answerLangCodes.length === 0
      ? audioList
      : audioList.filter(
          (p: any) =>
            Array.isArray(p.prompt_url) &&
            p.prompt_url.some(
              (e: any) =>
                e.status === 1 && answerLangCodes.includes(e.language),
            ),
        );

  const handleAudioChange = (e: any) => {
    const selected = audioList.find(
      (a: any) => a.id.toString() === e.target.value,
    );
    if (!selected) {
      setDraftAction((prev: any) => ({
        ...prev,
        id: '',
        audio: '',
        name: '',
        duration: '',
        languages: [],
      }));
      setDraftLanguage('');
      return;
    }
    const approvedLangs = Array.isArray(selected.prompt_url)
      ? selected.prompt_url.filter(
          (en: any) =>
            en.status === 1 &&
            (answerLangCodes.length === 0 ||
              answerLangCodes.includes(en.language)),
        )
      : [];
    if (hasLangParent) {
      // Downstream of a Language node — keep every approved language variant, no manual pick.
      setDraftAction({
        type: 'play',
        id: selected.id,
        name: selected.prompt_name,
        duration: selected.prompt_duration || '',
        languages: approvedLangs
          .filter((en: any) => en.status === 1)
          .map((en: any) => ({
            language: en.language,
            url: en.url,
            duration: en.duration ?? selected.prompt_duration ?? 0,
            status: en.status,
          })),
      });
      setDraftLanguage('');
      return;
    }
    const autoLang = approvedLangs.length === 1 ? approvedLangs[0].language : '';
    const autoAudio = approvedLangs.length === 1 ? approvedLangs[0].url : '';
    setDraftAction({
      type: 'play',
      id: selected.id,
      audio: autoAudio,
      duration: selected.prompt_duration || '',
      name: selected.prompt_name,
      language: autoLang,
    });
    setDraftLanguage(autoLang);
  };

  const handleLanguageChange = (e: any) => {
    const lang = e.target.value;
    setDraftLanguage(lang);
    const selectedPrompt = audioList.find(
      (a: any) => a.id?.toString() === draftAction?.id?.toString(),
    );
    const entry =
      lang && selectedPrompt && Array.isArray(selectedPrompt.prompt_url)
        ? selectedPrompt.prompt_url.find((en: any) => en.language === lang)
        : null;
    setDraftAction((prev: any) => ({
      ...prev,
      audio: entry?.url || '',
      language: lang,
    }));
  };

  const openModal = () => {
    const answerNode = getNodes().find((n: any) => n.type === 'answerNode');
    const _answerCodes = answerNode?.data?.languages || [];
    const _langNode = findUpstreamLanguageNode(id, getNodes, getEdges);
    const _langCodes =
      _langNode &&
      Array.isArray(_langNode.data?.selectedLanguages) &&
      _langNode.data.selectedLanguages.length > 0
        ? _langNode.data.selectedLanguages
        : null;
    setHasLangParent(!!_langNode);
    setAnswerLangCodes(_langCodes ?? _answerCodes);
    const _ancestorVars = getAncestorVariables();
    setAncestorVars(_ancestorVars);
    setDraftAction(action);
    setDraftLanguage(action.language || '');
    setDraftPromptPosition(action.prompt_position || 'before');
    setDraftPlayType(data.playType || 'recorded');
    setDraftTtsText(data.ttsText || '');
    setSelectedVar('');

    // The TTS voice profile is fixed at the Answer node — stamp it onto every
    // variable's config here so the table has no per-variable profile picker.
    const _answerProfile = answerNode?.data?.ttsProfile || null;
    setAnswerTtsProfile(_answerProfile);
    const savedConfig = data.variableTtsConfig || {};
    const mergedConfig: any = {};
    _ancestorVars.forEach((v: any) => {
      const existing = savedConfig[v.name] || {};
      mergedConfig[v.name] = {
        ...existing,
        profileId: _answerProfile?.id || '',
        profile: _answerProfile,
        language: existing.language || _answerProfile?.language || '',
      };
    });
    setVariableTtsConfig(mergedConfig);
    setErrors({});
    setModalIsOpen(true);
  };

  const handleClose = () => {
    setDraftAction(action);
    setDraftLanguage(action.language || '');
    setDraftPromptPosition(action.prompt_position || 'before');
    setDraftPlayType(data.playType || 'recorded');
    setDraftTtsText(data.ttsText || '');
    setSelectedVar('');
    setAncestorVars([]);
    setVariableTtsConfig(data.variableTtsConfig || {});
    setErrors({});
    setModalIsOpen(false);
  };

  const handleSave = () => {
    if (draftPlayType === 'tts') {
      if (!draftTtsText?.trim()) {
        setErrors({ ttsText: 'TTS text is required' });
        return;
      }
      const trimmedText = draftTtsText.trim();
      const seenNames = new Set();
      const usedVariables = ancestorVars
        .filter((v: any) => {
          if (seenNames.has(v.name)) return false;
          seenNames.add(v.name);
          return true;
        })
        .map((v: any) => ({ ...v, enable: trimmedText.includes(v.name) }));
      // Kept separate from usedVariables (node-produced variables) so the
      // existing shape the engine reads is unchanged.
      const usedFlowVariables = ttsFlowVariables
        .filter((v: any) => trimmedText.includes(flowVariableToken(v)))
        .map((v: any) => ({
          name: flowVariableToken(v),
          variableId: v.id,
          variableName: v.name,
          variableValue: v.value,
        }));
      const langByVar: any = {};
      Object.entries(variableTtsConfig || {}).forEach(
        ([name, cfg]: any) => {
          if (cfg?.language) langByVar[name] = cfg.language;
        },
      );
      const SAY_AS_LANG_RE =
        /<say-as\s+interpret-as="([^"]*)"\s+lang="([^"]*)"\s+rate="([^"]*)">([\s\S]*?)<\/say-as>/g;
      const finalTtsText = trimmedText.replace(
        SAY_AS_LANG_RE,
        (_full: any, interpretAs: any, lang: any, rate: any, inner: any) => {
          const varName = Object.keys(langByVar).find((v: any) =>
            inner.includes(v),
          );
          const useLang = varName ? langByVar[varName] : lang;
          return `<say-as interpret-as="${interpretAs}" lang="${useLang}" rate="${rate}">${inner}</say-as>`;
        },
      );
      updateNode({
        playType: 'tts',
        ttsText: finalTtsText,
        usedVariables,
        usedFlowVariables,
        action: {},
        variableTtsConfig,
      });
    } else {
      if (!passToNextNode && !draftAction?.id) {
        setErrors({ audio: 'Audio is required' });
        return;
      }
      if (!hasLangParent && draftAction?.id && !draftLanguage) {
        setErrors({ language: 'Please select a language' });
        return;
      }
      updateNode({
        playType: 'recorded',
        ttsText: '',
        action: {
          ...draftAction,
          ...(passToNextNode ? { prompt_position: draftPromptPosition } : {}),
        },
      });
    }
    toast.success('Details updated successfully.');
    setModalIsOpen(false);
  };

  const handleRemoveNode = () => {
    if (data.parent === 'gather Node' && data.idToRemove) {
      setNodes((nodes: any[]) =>
        nodes.map((node: any) => {
          if (!node.data.selectedHandles) return node;
          const updatedHandles = { ...node.data.selectedHandles };
          const updatedSmsData = { ...node.data.smsData };
          delete updatedHandles[data.idToRemove];
          if (updatedSmsData) {
            const parts = data.idToRemove.split('-');
            const handleIndex = parts[parts.length - 1];
            if (handleIndex) delete updatedSmsData[`DTMF_${handleIndex}`];
          }
          return {
            ...node,
            data: {
              ...node.data,
              selectedHandles: updatedHandles,
              smsData: updatedSmsData,
            },
          };
        }),
      );
    }
    onRemove(id);
  };

  const renderListenBtn = (url: any, name: any) =>
    url ? (
      <button
        type="button"
        className="ivr-drawer-play-btn"
        title="Listen"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() =>
          setDrawerPreview({ open: true, url, name: name || 'Audio' })
        }
        style={{ marginLeft: 8 }}
      >
        ▶
      </button>
    ) : null;

  return (
    <div className="ivrn" style={{ '--node-color': '#2563eb' } as any}>
      <span
        className="ivrn-icon"
        style={{ background: '#2563eb', color: '#fff' }}
        onClick={triggerHandleConnect}
      >
        <Volume2 size={22} strokeWidth={2} />
      </span>
      <Handle
        type="target"
        position={'left' as any}
        style={{ background: '#2563eb', top: '65%' }}
      />
      <Handle
        type="source"
        position={'right' as any}
        style={{ background: '#2563eb' }}
      />

      <div className="ivrn-head">
        <span className="ivrn-title">Play Audio</span>
      </div>
      <div className="ivrn-body">
        {audioName ? (
          <span className="ivrn-val">
            <Music size={10} style={{ marginRight: 4, flexShrink: 0 }} />
            {audioName}
          </span>
        ) : (
          <span className="ivrn-hint">Click ⚙ to configure audio</span>
        )}
      </div>

      <div className="ivrn-actions">
        <button
          className="ivrn-btn ivrn-btn--cfg"
          title="Configure"
          onClick={openModal}
        >
          <Settings size={14} strokeWidth={2} />
        </button>
        {audioUrl && (
          <button
            className="ivrn-btn ivrn-btn--play"
            title="Preview"
            onClick={() => setPlayModalOpen(true)}
          >
            <Play size={14} strokeWidth={2} />
          </button>
        )}
        <button
          className="ivrn-btn ivrn-btn--del"
          title="Remove"
          onClick={handleRemoveNode}
        >
          <Trash2 size={14} strokeWidth={2} />
        </button>
      </div>

      {/* Modals — render via React portals, safe inside node root */}
      <IvrDrawer
        show={modalIsOpen}
        onHide={handleClose}
        title="Play"
        subtitle="Configure audio playback — use a recorded prompt or text-to-speech."
        width={640}
        footer={
          <>
            <Button variant="secondary" onClick={handleClose}>
              Close
            </Button>
            <Button onClick={handleSave}>Save</Button>
          </>
        }
      >
        <Form>
          {/* Play Type */}
          <Form.Group className="mb-3">
            <Form.Label style={{ fontWeight: 600 }}>Play Type</Form.Label>
            <div style={{ display: 'flex', gap: '24px', marginTop: '4px' }}>
              <Form.Check
                type="radio"
                id="play-type-recorded"
                name="playType"
                label="Recorded"
                checked={draftPlayType === 'recorded'}
                onChange={() => {
                  setDraftPlayType('recorded');
                  setErrors({});
                }}
              />
              <Form.Check
                type="radio"
                id="play-type-tts"
                name="playType"
                label="TTS"
                checked={draftPlayType === 'tts'}
                onChange={() => {
                  setDraftPlayType('tts');
                  setErrors({});
                }}
              />
            </div>
          </Form.Group>

          {draftPlayType === 'recorded' ? (
            <>
              <Form.Group>
                <Form.Label>
                  Select Audio
                  {!passToNextNode && (
                    <span style={{ color: 'red' }}> *</span>
                  )}
                  {passToNextNode && (
                    <span
                      style={{
                        color: '#6c757d',
                        fontSize: '12px',
                        marginLeft: '6px',
                      }}
                    >
                      (Optional)
                    </span>
                  )}
                </Form.Label>
                <Form.Control
                  as="select"
                  value={draftAction?.id || ''}
                  onChange={handleAudioChange}
                >
                  <option value="">Select Audio</option>
                  {displayAudioList.map((item: any) => (
                    <option key={item.id} value={item.id}>
                      {item.prompt_name}
                    </option>
                  ))}
                </Form.Control>
                {errors.audio && (
                  <Form.Text className="text-danger">{errors.audio}</Form.Text>
                )}
              </Form.Group>
              {renderListenBtn(
                draftAction?.audio || draftAction?.languages?.[0]?.url || '',
                draftAction?.name,
              )}
              {hasLangParent ? (
                Array.isArray(draftAction?.languages) &&
                draftAction.languages.length > 0 && (
                  <div
                    style={{
                      marginTop: 10,
                      fontSize: 12,
                      color: '#0f766e',
                      background: '#f0fdfa',
                      border: '1px solid #99f6e4',
                      borderRadius: 8,
                      padding: '8px 12px',
                    }}
                  >
                    This node is downstream of a Language node — all{' '}
                    {draftAction.languages.length} approved language
                    {draftAction.languages.length !== 1 ? 's' : ''} (
                    {draftAction.languages
                      .map((l: any) => l.language?.toUpperCase())
                      .join(', ')}
                    ) will be saved automatically.
                  </div>
                )
              ) : (
                (() => {
                  const sel = audioList.find(
                    (a: any) =>
                      a.id?.toString() === draftAction?.id?.toString(),
                  );
                  const langs = Array.isArray(sel?.prompt_url)
                    ? sel.prompt_url.filter(
                        (e: any) =>
                          e.status === 1 &&
                          (answerLangCodes.length === 0 ||
                            answerLangCodes.includes(e.language)),
                      )
                    : [];
                  if (!sel || langs.length === 0) return null;
                  return (
                    <Form.Group style={{ marginTop: 12 }}>
                      <Form.Label>
                        Select Audio Language{' '}
                        <span style={{ color: 'red' }}>*</span>
                      </Form.Label>
                      <Form.Control
                        as="select"
                        value={draftLanguage}
                        onChange={handleLanguageChange}
                      >
                        <option value="">Select Language</option>
                        {langs.map((e: any) => (
                          <option key={e.language} value={e.language}>
                            {e.language?.toUpperCase()}
                            {e.name ? ` — ${e.name}` : ''}
                          </option>
                        ))}
                      </Form.Control>
                      {errors.language && (
                        <Form.Text className="text-danger">
                          {errors.language}
                        </Form.Text>
                      )}
                    </Form.Group>
                  );
                })()
              )}
            </>
          ) : (
            <>
              {/* Insert Variable */}
              {(ancestorVars.length > 0 || ttsFlowVariables.length > 0) && (
                <Form.Group className="mb-3">
                  <Form.Label>Insert Variable</Form.Label>
                  <Form.Select
                    value={selectedVar}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val) {
                        setDraftTtsText((prev: string) => prev + val);
                        setSelectedVar('');
                      }
                    }}
                  >
                    <option value="">Select variable to insert</option>
                    {ttsFlowVariables.length > 0 && (
                      <optgroup label="Flow Variables">
                        {ttsFlowVariables.map((v: any) => (
                          <option key={`fv-${v.id}`} value={flowVariableToken(v)}>
                            {v.name}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    {ancestorVars.length > 0 && (
                      <optgroup label="From Previous Nodes">
                        {ancestorVars.map((v: any, i: number) => (
                          <option key={i} value={v.name}>
                            {v.name}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </Form.Select>
                </Form.Group>
              )}

              {/* Per-variable TTS configuration table */}
              {ancestorVars.length > 0 && (
                <div className="mb-3">
                  <div
                    style={{
                      fontSize: '13px',
                      fontWeight: 600,
                      marginBottom: '8px',
                      color: '#212529',
                    }}
                  >
                    Variable TTS Configuration
                  </div>

                  {answerTtsProfile ? (
                    <div
                      style={{
                        marginBottom: 8,
                        fontSize: 12,
                        color: '#0f766e',
                        background: '#f0fdfa',
                        border: '1px solid #99f6e4',
                        borderRadius: 8,
                        padding: '8px 12px',
                      }}
                    >
                      Using TTS Voice Profile from the Answer node —{' '}
                      <strong>
                        {answerTtsProfile.flow_name ||
                          `Profile #${answerTtsProfile.id}`}
                      </strong>{' '}
                      ({answerTtsProfile.gender} | {answerTtsProfile.language}).
                      Change it there to update it for every Play node.
                    </div>
                  ) : (
                    <div
                      style={{
                        marginBottom: 8,
                        fontSize: 12,
                        color: '#92400e',
                        background: '#fffbeb',
                        border: '1px solid #fde68a',
                        borderRadius: 8,
                        padding: '8px 12px',
                      }}
                    >
                      No TTS Voice Profile is set on the Answer node yet. Open
                      the Answer node's settings to pick one.
                    </div>
                  )}

                  <div
                    style={{
                      border: '1px solid #dee2e6',
                      borderRadius: '6px',
                      overflow: 'hidden',
                    }}
                  >
                    {/* Header */}
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr 1fr',
                        gap: '8px',
                        padding: '6px 12px',
                        background: '#f8f9fa',
                        borderBottom: '1px solid #dee2e6',
                      }}
                    >
                      {['Variable', 'Language', 'Gender'].map((h, i) => (
                        <span
                          key={i}
                          style={{
                            fontSize: '11px',
                            fontWeight: 600,
                            color: '#6c757d',
                            textTransform: 'uppercase',
                            letterSpacing: '0.4px',
                          }}
                        >
                          {h}
                        </span>
                      ))}
                    </div>
                    {/* Rows */}
                    {ancestorVars.map((v: any, i: number) => {
                      const cfg = variableTtsConfig[v.name];
                      const profile = cfg?.profile;
                      const effectiveLanguage =
                        cfg?.language ?? profile?.language ?? '';
                      return (
                        <div
                          key={i}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr 1fr',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '7px 12px',
                            borderBottom:
                              i < ancestorVars.length - 1
                                ? '1px solid #f0f0f0'
                                : 'none',
                            background: profile ? '#f8fff8' : '#fff',
                          }}
                        >
                          <span
                            style={{
                              fontFamily: 'monospace',
                              fontSize: '12px',
                              color: '#0c7a8a',
                              fontWeight: 600,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            title={v.name}
                          >
                            {v.name}
                          </span>
                          <select
                            value={effectiveLanguage}
                            onChange={(e) => {
                              const lang = e.target.value;
                              setVariableTtsConfig((prev: any) => ({
                                ...prev,
                                [v.name]: {
                                  profileId: answerTtsProfile?.id || '',
                                  profile: answerTtsProfile,
                                  ...(prev[v.name] || {}),
                                  language: lang,
                                },
                              }));
                            }}
                            style={{ fontSize: '12px', width: '100%' }}
                          >
                            <option value="">Select language...</option>
                            {ttsLanguages.map((l: any) => (
                              <option key={l.code} value={l.code}>
                                {l.language}
                              </option>
                            ))}
                          </select>
                          <span
                            style={{
                              fontSize: '12px',
                              color: profile ? '#212529' : '#adb5bd',
                              textTransform: 'capitalize',
                            }}
                          >
                            {profile
                              ? String(profile.gender).toUpperCase() ===
                                'FEMALE'
                                ? 'Female'
                                : 'Male'
                              : '—'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* TTS Text */}
              <Form.Group>
                <Form.Label>
                  TTS Text <span style={{ color: 'red' }}>*</span>
                </Form.Label>
                <SsmlTextEditor
                  value={draftTtsText}
                  onChange={setDraftTtsText}
                  lang="en"
                  rate={1.0}
                  placeholder="Type your message here. Use the dropdown above to insert a variable."
                />
                {errors.ttsText && (
                  <Form.Text className="text-danger">
                    {errors.ttsText}
                  </Form.Text>
                )}
              </Form.Group>
            </>
          )}
        </Form>
      </IvrDrawer>

      <AudioPreviewModal
        show={playModalOpen}
        onClose={() => setPlayModalOpen(false)}
        audioUrl={audioUrl}
        title={audioName}
      />

      <AudioPreviewModal
        show={drawerPreview.open}
        onClose={() =>
          setDrawerPreview({ open: false, url: '', name: '' })
        }
        audioUrl={drawerPreview.url}
        title={drawerPreview.name}
      />
    </div>
  );
};

export default PlayNode;
